import sentry from '../../sentry';
import { Container, Divider, Grid, Spinner, Center } from '@chakra-ui/react';
import Breadcrumbs from '../../common/Breadcrumbs';
import { useContext, useEffect } from 'react';
import { MeContext } from '../../context/MeContext';
import DashboardNoPassTemplates from '../../components/client/dashboard/DashboardNoPassTemplates';
import { HttpContext } from '../../context/HttpContext';
import useOrganizationStatus from '../../hooks/useOrganisationStatus';
import KeyStats from '../../components/client/dashboard/KeyStats';
import PassesStats from '../../components/client/dashboard/PassesStats';
import ScansStats from '../../components/client/dashboard/ScansStats';
import MessagingStats from '../../components/client/dashboard/MessagingStats';
import ErrorToast from '../../common/ErrorToast';
import { ClientDashboardStatsContext } from '../../store/client/ClientDashboardStatsContext';
import { getErrorResponsePayload } from '../../utils/ajax';
import { useToast } from '@chakra-ui/react';
import moment from 'moment';
import { useBilling } from '../../store/client/BillingContext';
import TrialBanner from '../../components/common/TrialBanner';
import PageHeadingWithTrialProgress from '../../components/client/dashboard/PageHeadingWithTrialProgress';
import OnboardingChecklist from '../../components/client/onboarding/OnboardingChecklist';
import { useTrackSetupReached } from '../../components/client/onboarding/useTrackSetupReached';

const DEFAULT_TIME_RANGE = '7days';

function ClientDashboardPage() {
  const meCtx = useContext(MeContext);
  useTrackSetupReached();
  const authAxios = useContext(HttpContext).authAxios;
  const { shouldRender, components } = useOrganizationStatus();
  const {
    updateDashboardData,
    dashboardState,
    updateSectionTimeRange,
    loadingStates,
    setLoading,
  } = useContext(ClientDashboardStatsContext);
  const toast = useToast();
  const { filterEntity } = meCtx.state;
  const { isLoading } = useBilling();

  const breadcrumbLinks = [
    {
      linkName: 'Home',
      linkUrl: '/',
    },
    {
      linkName: 'analytics',
      linkUrl: null,
    },
  ];

  const prepareTimeQuery = (section, timeRange) => {
    if (!section && !timeRange) return '';

    if (timeRange === 'all' && section) {
      return `&${section}From=all`;
    }

    let fromDate;
    if (timeRange === '7days') {
      fromDate = moment().subtract(7, 'days');
    } else if (timeRange === '1month') {
      fromDate = moment().subtract(1, 'months');
    } else {
      fromDate = moment().subtract(6, 'months').startOf('month');
    }
    return `&${section}From=${encodeURIComponent(fromDate)}`;
  };

  const prepareInitialQuery = (timeRange) => {
    return (
      prepareTimeQuery('pass', timeRange) +
      prepareTimeQuery('scan', timeRange) +
      prepareTimeQuery('messaging', timeRange) +
      '&keyStats=true'
    );
  };

  const getData = async (section = null, timeRange = null) => {
    if (!shouldRender) {
      console.log(
        'Setup not confirmed or organisation is not active, skipping data fetch'
      );
      return;
    }

    if (section) {
      setLoading(section, true);
    } else {
      setLoading('global', true);
    }
    const timeQuery = section
      ? prepareTimeQuery(section, timeRange)
      : prepareInitialQuery(DEFAULT_TIME_RANGE);

    try {
      let response;
      const baseUrl = filterEntity?.uuid
        ? `api/v1/entities/${filterEntity.uuid}/dashboard`
        : `api/v1/dashboard`;

      const url = `${baseUrl}?${timeQuery}`;

      response = await Promise.all([authAxios.get(`${url}`)]);

      const data = response[0].data;

      updateDashboardData(data);

      if (section) {
        setLoading(section, false);
      } else {
        setLoading('global', false);
      }
    } catch (error) {
      if (section) {
        setLoading(section, false);
      } else {
        setLoading('global', false);
      }
      console.log(error);
      const { code } = getErrorResponsePayload(error);
      code !== 401 &&
        toast({
          render: (props) => (
            <ErrorToast error={error} onClose={props.onClose} />
          ),
        });
    }
  };

  useEffect(() => {
    updateSectionTimeRange('pass', DEFAULT_TIME_RANGE);
    updateSectionTimeRange('scan', DEFAULT_TIME_RANGE);
    updateSectionTimeRange('messaging', DEFAULT_TIME_RANGE);
    getData();
  }, [authAxios, shouldRender, filterEntity]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner />
      </Center>
    );
  }

  return (
    <Container pb={6} overflow="visible">
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      <PageHeadingWithTrialProgress
        title="Analytics"
        buttonVariant={
          (meCtx.state.onboardingChecklistEnabled &&
            !meCtx.state.onboardingCompleted) ||
          (dashboardState.totalPassTemplates === 0 && !loadingStates.global)
            ? 'secondary'
            : 'gradient'
        }
      />
      <Divider border="1px solid #D9D9D9" mb={6} />
      <TrialBanner mb={6} />
      <OnboardingChecklist />
      {components[0]}

      <KeyStats isLoading={loadingStates.global} />
      <Grid
        templateColumns={{
          base: 'repeat(1, 1fr)',
          '2xl':
            dashboardState.totalPassTemplates > 0 || loadingStates.global
              ? 'repeat(1, 1fr)'
              : 'repeat(2, 1fr)',
        }}
        gap={8}
      >
        {dashboardState.totalPassTemplates === 0 && !loadingStates.global && (
          <DashboardNoPassTemplates />
        )}
        <PassesStats getData={getData} />
        <ScansStats getData={getData} />
        <MessagingStats getData={getData} />
      </Grid>
    </Container>
  );
}

export default ClientDashboardPage;
